import { useState, useRef, useContext } from 'react';
import {Dialog,DialogContent,Skeleton,IconButton,Box,Tooltip,Stack, Alert} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { Download } from '@mui/icons-material';
import { useMedicalCertificateService } from '../../hooks/services/useMedicalCertificateService';
import { hasAccessToAction } from '../../util/hasAccessTo';
import UserContext from '../../context/user/UserContext';

interface ImageViewerProps {
    medicalCertificateId: string;
    imageUrl: string;
    alt?: string;
    tooltipTitle?: string;
    renderTrigger?: (onClick: () => void) => React.ReactNode;
}

interface DownloadResource {
  medicalCertificateid: string;
  filename: string;
  token: string;
  description: string;
}

const ImageViewer = ({ medicalCertificateId, imageUrl, alt, tooltipTitle, renderTrigger }: ImageViewerProps) => {

    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [scale, setScale] = useState(1);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [error, setError] = useState<string | null>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const imageRef = useRef<HTMLImageElement>(null);
    const { state } = useContext(UserContext);

    const canView = hasAccessToAction(
        ["followup-atestado-web"],
        state.resourcesWithActions, 
        "Editar"
    );
      
    const storage = sessionStorage.getItem('user');
    let parsedStorage;
    if (storage) parsedStorage = JSON.parse(storage);
    const token = parsedStorage.access_token;

    const handleOpen = () => {
    if (buttonRef.current) buttonRef.current.blur();
    setOpen(true);
    setLoading(true);
    setScale(1);
    setPosition({ x: 0, y: 0 });
  };

    const handleClose = () => {
        setError(null);
        setOpen(false);
    };

    const handleZoomIn = () => {
        setScale(prev => Math.min(prev + 0.5, 5));
    };

    const handleZoomOut = () => {
        setScale(prev => {
            const newScale = Math.max(prev - 0.5, 1); 
            if (newScale === 1) {
                setPosition({ x: 0, y: 0 }); // Reset posição ao voltar ao zoom original
            }
            return newScale;
        });
    };

    const handleReset = () => {
        setScale(1);
        setPosition({ x: 0, y: 0 });
    };

    const getFilename = (imageUrl: string) => {
        const url = imageUrl;
        const fileName = url.split('/')[url.split('/').length - 1];

        return fileName.substring(33, 999);
    };
    
    const medicalCertificateRequest = useMedicalCertificateService();

    const attachment: DownloadResource = {
        medicalCertificateid: medicalCertificateId,
        filename: imageUrl,
        token: token,
        description: tooltipTitle || "teste"
    }

    const handleDownload = async (attachment: DownloadResource) => {
        try {
            const result = await medicalCertificateRequest.getDownloadImage(
                attachment.medicalCertificateid,
                attachment.filename,
                attachment.token,
                attachment.description,
            );

            const url = window.URL.createObjectURL(result);
           
            const link = document.createElement('a');
            link.href = url;
            link.download = attachment.description || getFilename(attachment.filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            setError(error instanceof Error ? error.message : 'Ocorreu um erro ao baixar o arquivo.');
        } finally {
            setLoading(false);
        }
    };

    const handleWheel = (e: React.WheelEvent) => {
        const delta = e.deltaY > 0 ? -0.2 : 0.2;
        setScale(prev => Math.min(Math.max(prev + delta, 1), 5));
    };

    const handleMouseDown = (e: React.MouseEvent) => {
        if (scale > 1) {
            setIsDragging(true);
            setDragStart({
                x: e.clientX - position.x,
                y: e.clientY - position.y
            });
        }
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        if (isDragging && scale > 1) {
            setPosition({
                x: e.clientX - dragStart.x,
                y: e.clientY - dragStart.y
            });
        }
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    return (
        <>
            {renderTrigger ? (
                renderTrigger(handleOpen)
            ) : (
                    <Tooltip title={tooltipTitle} placement="left">
                        <IconButton
                            ref={buttonRef}
                            onClick={handleOpen}
                            size="small"
                            aria-label={tooltipTitle}
                            sx={{
                            color: 'action.active',
                            '&:hover': { color: '#2e7d32' },
                            transition: 'color 0.3s',
                            }}
                        >
                            <VisibilityIcon />
                        </IconButton>
                    </Tooltip>
            )}
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="xl"
                fullWidth
                disableRestoreFocus
            >
                <IconButton
                    onClick={handleClose}
                    aria-label="Fechar visualização"
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: 'white',
                        zIndex: 2,
                        bgcolor: 'rgba(0, 0, 0, 0.5)',
                        ':hover': {
                            bgcolor: 'rgba(0, 0, 0, 0.7)',
                        },
                    }}
                >
                    <CloseIcon />
                </IconButton>

                <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                        position: 'absolute',
                        bottom: 16,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 2,
                        bgcolor: 'rgba(0, 0, 0, 0.6)',
                        borderRadius: 2,
                        p: 1
                    }}
                >
                    <Tooltip title="Diminuir zoom">
                        <IconButton
                            onClick={handleZoomOut}
                            size="small"
                            sx={{ color: 'white' }}
                        >
                            <ZoomOutIcon />
                        </IconButton>
                    </Tooltip>

                    <Box
                        sx={{
                            color: 'white',
                            px: 2,
                            display: 'flex',
                            alignItems: 'center',
                            minWidth: 60,
                            justifyContent: 'center',
                            fontSize: '0.875rem'
                        }}
                    >
                        {Math.round(scale * 100)}%
                    </Box>

                    {/* <Tooltip title="Aumentar zoom"> */}
                        <IconButton
                            onClick={handleZoomIn}
                            size="small"
                            sx={{ color: 'white' }}
                        >
                            <ZoomInIcon />
                        </IconButton>
                    {/* </Tooltip> */}

                    <Tooltip title="Resetar visualização">
                        <IconButton
                            onClick={handleReset}
                            size="small"
                            sx={{ color: 'white' }}
                        >
                            <RestartAltIcon />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title="Download">
                        <IconButton
                            onClick={() => handleDownload(attachment)}
                            disabled={!canView}
                            size="small"
                            sx={{ color: 'white' }}
                        >
                            <Download/>
                        </IconButton>
                    </Tooltip>
                </Stack>

                <DialogContent
                    sx={{
                        p: 0,
                        overflow: 'hidden',
                        cursor: scale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default'
                    }}
                    onWheel={handleWheel}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                >
                    {error && (
                        <Alert severity="error" onClose={() => setError(null)}>
                            {error}
                        </Alert>
                    )}
                    <Box
                        sx={{
                            position: 'relative',
                            width: '100%',
                            minHeight: 400,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        {loading && (
                            <Skeleton
                                variant="rectangular"
                                width="100%"
                                height={600}
                                animation="wave"
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                }}
                            />
                        )}
                        <img
                            ref={imageRef}
                            src={imageUrl}
                            alt={alt}
                            onLoad={() => setLoading(false)}
                            onError={() => setLoading(false)}
                            style={{
                                maxWidth: '95%',
                                maxHeight: '85vh',
                                objectFit: 'contain',
                                display: loading ? 'none' : 'block',
                                transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
                                transition: isDragging ? 'none' : 'transform 0.2s ease-out',
                                userSelect: 'none'
                            }}
                            draggable={false}
                        />
                    </Box>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default ImageViewer;